import type { Aabb, InteriorTemplate, StaticCollider, Vec2 } from "@townsy/contracts";
import { YAW_NORTH, YAW_SOUTH, aabb } from "../geometry";
import { obbIntersectsObb, type Obb } from "../vehicles";
import { fixture } from "./common";

/**
 * Monster Truck Arena (ADR-0016, founder request 2026-10-13: "drive monster trucks on a track with jumps and fun things
 * and race others"). ONE shared room (170 x 120 m) hosting a dirt circuit; this file is the shared geometry and the
 * deterministic truck model used by the realtime game (authority) and the client's prediction and renderer.
 *
 * Conventions as in the rest of @townsy/world: metres, x east, z north, heading = yaw (0 = +z, +PI/2 = +x), a truck's
 * FORWARD is (sin h, cos h) and its LEFT is (-cos h, sin h). The driver sits on the left.
 *
 * Layout (north is up):
 *  - Walkway / pit (walkers only): the south strip z -60..-51 across the room, widening to the trackside barrier
 *    (z -43) west and east of the pit. Six trucks park side by side in bays north of the pit wall, facing north; a
 *    narrow boarding pier runs along each truck's driver side (west) so a walker reaches the driver's door over the
 *    pier rail. Walkers never reach the track: pit wall, pier rails and the trackside barrier are template colliders.
 *  - Track (trucks only): a closed counter-clockwise circuit 16 m wide around a central infield (start/finish on the
 *    south straight heading east, ~440 m along the centre line). Features: a kicker ramp on the east side, a mud pit
 *    (with a clean lane along the outer wall), a table-top jump across the north straight, a crushed-car pile on the
 *    west side and a second kicker before the last corner. Five ordered checkpoints (0 = the start/finish line).
 *  - Stands: grandstands behind the north and side walls (decorative, beyond the barriers).
 */
export const TRUCK_ARENA_BOUNDS: Aabb = aabb(-85, -60, 85, 60);
const B = TRUCK_ARENA_BOUNDS;

/** Pit geometry (walker side). */
export const PIT = {
  /** Pit wall between the walkway and the bays. */
  wallSouthZ: -51.0,
  wallNorthZ: -50.6,
  /** Trackside barrier: its north face is the south edge of the track. */
  barrierSouthZ: -43.4,
  trackEdgeZ: -43.0,
  /** West edge of the first boarding pier; every bay + pier pair takes `pitch` metres. */
  firstX: -16.9,
  pitch: 5.6,
  railThickness: 0.2,
  pierInner: 1.6,
} as const;

/** Pier i's inner (walkable) x range; pier 0 merges with the west walkway. */
export function pierSpan(i: number): { minX: number; maxX: number } {
  const p = PIT.firstX + PIT.pitch * i;
  return { minX: p + PIT.railThickness, maxX: p + PIT.railThickness + PIT.pierInner };
}

/** x range of bay i (between pier i's east rail and the next pier's west rail). */
export function baySpan(i: number): { minX: number; maxX: number } {
  const p = PIT.firstX + PIT.pitch * i;
  return { minX: p + 2 * PIT.railThickness + PIT.pierInner, maxX: p + PIT.pitch };
}

// ---------------------------------------------------------------------------------------------
// Trucks
// ---------------------------------------------------------------------------------------------

/**
 * One monster truck body: 6 m long and 3 m wide over the tyres, 3.6 m wheelbase, 0.85 m wheel radius. The collision
 * box is the body; truck-to-truck contact uses two circles along the centre line.
 */
export const TRUCK_SPEC = {
  halfLength: 3.0,
  halfWidth: 1.5,
  wheelbase: 3.6,
  /** Driver's seat / door, measured forward from the centre. */
  seatForward: 0.3,
  seatLeft: 0.45,
  wheelRadius: 0.85,
  /** Height of the cab floor and the roof above the ground the wheels stand on (renderer and name tags). */
  cabFloorY: 2.05,
  roofY: 3.35,
  circleOffset: 1.5,
  circleRadius: 1.5,
} as const;

/** Driving model (ADR-0016): faster and punchier than a town car (12 m/s). */
export const TRUCK_DRIVE = {
  maxForward: 17,
  maxReverse: 5,
  accel: 7,
  brake: 14,
  coastDecel: 2,
  maxSteerRad: 0.6,
  /** Steering is divided by (1 + |speed| / steerFadeSpeed). */
  steerFadeSpeed: 10,
  /** Below this the truck does not turn, and counts as stopped (getting in needs it). */
  minTurnSpeed: 0.3,
} as const;

/** Vertical motion, mud and contact. */
export const TRUCK_PHYSICS = {
  gravity: 16,
  /** A grounded truck leaves the ground when its ballistic height is this far above the surface. */
  takeoffEpsilon: 0.05,
  /** The surface under a grounded truck may rise at most this much in one step (a steeper face blocks it). */
  maxStep: 0.45,
  mudMaxSpeed: 6,
  /** Deceleration toward mudMaxSpeed in the mud (m/s^2). */
  mudDrag: 20,
  /** Throttle acceleration is multiplied by this in the mud. */
  mudAccelFactor: 0.5,
  /** Speed kept after touching another truck. */
  contactSpeedFactor: 0.6,
  /** Trucks more than this apart in height do not touch (one flies over the other). */
  passOverHeight: 2.0,
  /** Crawl speed kept when only a rotation is possible (scraping a wall). */
  scrapeSpeed: 1,
} as const;

export const TRUCK_STOPPED_SPEED = TRUCK_DRIVE.minTurnSpeed;
/** Door reach, as for town cars (the client prompt uses exactly this; the server adds a lag allowance). */
export const TRUCK_DOOR_REACH_METRES = 1.4;
const DOOR_STANDOFF = 0.7;

/** Centre of a truck parked in its bay (facing north). */
export const TRUCK_BAY_Z = PIT.wallNorthZ + 0.4 + TRUCK_SPEC.halfLength;

export interface TruckPose {
  x: number;
  z: number;
  heading: number;
}

export interface TruckFleetEntry {
  /** Room-wide truck id ("t1".."t6"). */
  id: string;
  /** Number painted on the boards (1..6). */
  number: number;
  /** Paint index (the renderer's palette). */
  paint: number;
  /** Parking pose in its bay. */
  bay: TruckPose;
  /** Where its driver stands on the boarding pier (and is put when getting out). */
  boarding: Vec2;
}

export const TRUCK_FLEET: readonly TruckFleetEntry[] = Array.from({ length: 6 }, (_, i) => {
  const span = baySpan(i);
  const x = (span.minX + span.maxX) / 2;
  const pier = pierSpan(i);
  return {
    id: `t${i + 1}`,
    number: i + 1,
    paint: i,
    bay: { x, z: TRUCK_BAY_Z, heading: YAW_NORTH },
    boarding: { x: (pier.minX + pier.maxX) / 2, z: TRUCK_BAY_Z + TRUCK_SPEC.seatForward },
  };
});

export function truckEntry(id: string): TruckFleetEntry | undefined {
  return TRUCK_FLEET.find((t) => t.id === id);
}

function axes(h: number): { fx: number; fz: number; rx: number; rz: number } {
  const s = Math.sin(h);
  const c = Math.cos(h);
  return { fx: s, fz: c, rx: c, rz: -s };
}

/** A point `forward` along the heading and `left` to the driver's side. */
function truckPoint(p: TruckPose, forward: number, left: number): Vec2 {
  const { fx, fz, rx, rz } = axes(p.heading);
  return { x: p.x + fx * forward - rx * left, z: p.z + fz * forward - rz * left };
}

export function truckObb(p: TruckPose): Obb {
  return { cx: p.x, cz: p.z, halfWidth: TRUCK_SPEC.halfWidth, halfLength: TRUCK_SPEC.halfLength, heading: p.heading };
}

/** Where a walker stands to get in: left side, level with the seat, 0.7 m outside the body. */
export function truckDoorPoint(p: TruckPose): Vec2 {
  return truckPoint(p, TRUCK_SPEC.seatForward, TRUCK_SPEC.halfWidth + DOOR_STANDOFF);
}

/** Where the driver sits (the driver's avatar position while driving). */
export function truckSeatPoint(p: TruckPose): Vec2 {
  return truckPoint(p, TRUCK_SPEC.seatForward, TRUCK_SPEC.seatLeft);
}

/** The two contact circles (rear, front). */
export function truckCircles(p: TruckPose): [Vec2, Vec2] {
  return [truckPoint(p, -TRUCK_SPEC.circleOffset, 0), truckPoint(p, TRUCK_SPEC.circleOffset, 0)];
}

/**
 * True when a walker at `walker` may get into the truck: stopped and on the ground, within `reach` of the driver's door
 * and on the driver's side. Occupancy, rate limits and "already driving" are the server's checks.
 */
export function canEnterTruck(walker: Vec2, m: TruckMotion, reach = TRUCK_DOOR_REACH_METRES): boolean {
  if (!Number.isFinite(walker.x) || !Number.isFinite(walker.z)) return false;
  if (!(Math.abs(m.speed) < TRUCK_STOPPED_SPEED) || m.airborne) return false;
  const door = truckDoorPoint(m);
  if (Math.hypot(walker.x - door.x, walker.z - door.z) > reach) return false;
  const { rx, rz } = axes(m.heading);
  return -((walker.x - m.x) * rx + (walker.z - m.z) * rz) > 0;
}

/** True when the truck stands (close enough to) exactly in its bay. */
export function inOwnBay(entry: TruckFleetEntry, p: TruckPose): boolean {
  const dh = Math.abs(Math.atan2(Math.sin(p.heading - entry.bay.heading), Math.cos(p.heading - entry.bay.heading)));
  return Math.hypot(p.x - entry.bay.x, p.z - entry.bay.z) < 0.75 && dh < 0.2;
}

// ---------------------------------------------------------------------------------------------
// Track
// ---------------------------------------------------------------------------------------------

export type BarrierKind = "concrete" | "tyres" | "rail" | "pit";

/** A truck collider (oriented box on the ground). Pit barriers are also walker colliders (template colliders). */
export interface TruckBarrier {
  id: string;
  kind: BarrierKind;
  obb: Obb;
}

const obbOfAabb = (b: Aabb): Obb => ({ cx: (b.minX + b.maxX) / 2, cz: (b.minZ + b.maxZ) / 2, halfWidth: (b.maxX - b.minX) / 2, halfLength: (b.maxZ - b.minZ) / 2, heading: 0 });

/** A barrier along the segment a -> b, `thickness` thick, its face on the segment and its body on the `side` (+1 = right of a->b). */
function wallAlong(id: string, kind: BarrierKind, a: Vec2, b: Vec2, thickness: number, side: 1 | -1): TruckBarrier {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  const heading = Math.atan2(dx, dz);
  const { rx, rz } = axes(heading);
  const off = (thickness / 2) * side;
  return { id, kind, obb: { cx: (a.x + b.x) / 2 + rx * off, cz: (a.z + b.z) / 2 + rz * off, halfWidth: thickness / 2, halfLength: len / 2, heading } };
}

/**
 * Walker colliders of the pit (template colliders): bay rails, pit wall (gaps onto the piers), trackside barrier, pier
 * caps. Ids start with `venue_` so the generic interior builder leaves them to the arena's own 3D.
 */
function pitColliders(): StaticCollider[] {
  const out: StaticCollider[] = [];
  const x0 = PIT.firstX;
  const t = PIT.railThickness;
  const rail = (id: string, x: number) => fixture(id, aabb(x, PIT.wallSouthZ, x + t, PIT.trackEdgeZ));
  for (let i = 0; i < 6; i++) {
    const p = x0 + PIT.pitch * i;
    if (i > 0) out.push(rail(`venue_pit_rail_${i}w`, p));
    out.push(rail(`venue_pit_rail_${i}e`, p + t + PIT.pierInner));
  }
  const end = x0 + PIT.pitch * 6;
  out.push(rail("venue_pit_rail_end", end));
  let start = x0 + t + PIT.pierInner;
  for (let i = 1; i < 6; i++) {
    const pier = pierSpan(i);
    out.push(fixture(`venue_pit_wall_${i}`, aabb(start, PIT.wallSouthZ, pier.minX, PIT.wallNorthZ)));
    start = pier.maxX;
  }
  out.push(fixture("venue_pit_wall_6", aabb(start, PIT.wallSouthZ, end + t, PIT.wallNorthZ)));
  out.push(fixture("venue_pit_track_wall_w", aabb(B.minX, PIT.barrierSouthZ, x0 + 2 * t + PIT.pierInner, PIT.trackEdgeZ)));
  for (let i = 1; i < 6; i++) {
    const p = x0 + PIT.pitch * i;
    out.push(fixture(`venue_pit_cap_${i}`, aabb(p, PIT.barrierSouthZ, p + 2 * t + PIT.pierInner, PIT.trackEdgeZ)));
  }
  out.push(fixture("venue_pit_track_wall_e", aabb(end, PIT.barrierSouthZ, B.maxX, PIT.trackEdgeZ)));
  return out;
}

export const TRUCK_PIT_COLLIDERS: readonly StaticCollider[] = pitColliders();

/** Outer limits of the track (inner faces) and the infield island. */
export const TRACK = {
  outerMinX: -82,
  outerMaxX: 82,
  outerMaxZ: 56,
  outerChamfer: 10,
  island: aabb(-66, -27, 66, 40),
  islandChamfer: 8,
  width: 16,
} as const;

function trackBarriers(): TruckBarrier[] {
  const out: TruckBarrier[] = TRUCK_PIT_COLLIDERS.map((c) => ({ id: c.id, kind: c.id.includes("rail") || c.id.includes("cap") ? "rail" : "pit", obb: obbOfAabb(c.aabb) }));
  const T = TRACK;
  const s = PIT.trackEdgeZ;
  const c = T.outerChamfer;
  // Outer concrete walls (body outside the track), clockwise seen from above: west, north, east, then the chamfers.
  out.push({ id: "outer_w", kind: "concrete", obb: obbOfAabb(aabb(B.minX, s - 0.4, T.outerMinX, B.maxZ)) });
  out.push({ id: "outer_e", kind: "concrete", obb: obbOfAabb(aabb(T.outerMaxX, s - 0.4, B.maxX, B.maxZ)) });
  out.push({ id: "outer_n", kind: "concrete", obb: obbOfAabb(aabb(B.minX, T.outerMaxZ, B.maxX, B.maxZ)) });
  // Corner chamfers (the face on the line, the body toward the corner).
  out.push(wallAlong("outer_sw", "concrete", { x: T.outerMinX, z: s + c }, { x: T.outerMinX + c, z: s }, 0.8, 1));
  out.push(wallAlong("outer_se", "concrete", { x: T.outerMaxX - c, z: s }, { x: T.outerMaxX, z: s + c }, 0.8, 1));
  out.push(wallAlong("outer_ne", "concrete", { x: T.outerMaxX, z: T.outerMaxZ - c }, { x: T.outerMaxX - c, z: T.outerMaxZ }, 0.8, 1));
  out.push(wallAlong("outer_nw", "concrete", { x: T.outerMinX + c, z: T.outerMaxZ }, { x: T.outerMinX, z: T.outerMaxZ - c }, 0.8, 1));
  // Infield island: tyre walls, counter-clockwise outline seen from above, body inside the island (left of travel).
  const i = T.island;
  const k = T.islandChamfer;
  const pts: Vec2[] = [
    { x: i.minX + k, z: i.minZ },
    { x: i.maxX - k, z: i.minZ },
    { x: i.maxX, z: i.minZ + k },
    { x: i.maxX, z: i.maxZ - k },
    { x: i.maxX - k, z: i.maxZ },
    { x: i.minX + k, z: i.maxZ },
    { x: i.minX, z: i.maxZ - k },
    { x: i.minX, z: i.minZ + k },
  ];
  const names = ["s", "se", "e", "ne", "n", "nw", "w", "sw"];
  for (let n = 0; n < pts.length; n++) out.push(wallAlong(`island_${names[n]}`, "tyres", pts[n]!, pts[(n + 1) % pts.length]!, 0.8, -1));
  return out;
}

/** Every truck collider: pit barriers, outer walls, corner chamfers, infield tyre walls. */
export const TRUCK_BARRIERS: readonly TruckBarrier[] = trackBarriers();

export type RampKind = "kicker" | "table" | "pile";

/** A height feature spanning the full track width. Heights are along `dir` (the direction trucks take off). */
export interface TruckRamp {
  id: string;
  kind: RampKind;
  rect: Aabb;
  /** Unit take-off direction along an axis: trucks climb the slope moving this way. */
  dir: Vec2;
  /** Lip (kicker), table top or hump crest height (m). */
  lipHeight: number;
  /** Lengths along `dir`: up-slope, flat top, down-slope (a kicker has only `up`; a pile is one smooth hump of `up`). */
  up: number;
  top: number;
  down: number;
  /** Marked landing zone after the lip (null for the pile). */
  landing: Aabb | null;
}

export const TRUCK_RAMPS: readonly TruckRamp[] = [
  { id: "kicker_east", kind: "kicker", rect: aabb(66, -22.5, 82, -17), dir: { x: 0, z: 1 }, lipHeight: 2.4, up: 5.5, top: 0, down: 0, landing: aabb(66, -13, 82, 0) },
  { id: "table_north", kind: "table", rect: aabb(-6, 40, 14, 56), dir: { x: -1, z: 0 }, lipHeight: 2.7, up: 6, top: 8, down: 6, landing: aabb(-21, 40, -6, 56) },
  { id: "car_pile", kind: "pile", rect: aabb(-82, 22, -66, 32), dir: { x: 0, z: -1 }, lipHeight: 1.3, up: 10, top: 0, down: 0, landing: null },
  { id: "kicker_west", kind: "kicker", rect: aabb(-82, -1, -66, 4), dir: { x: 0, z: -1 }, lipHeight: 2.2, up: 5, top: 0, down: 0, landing: aabb(-82, -16, -66, -5) },
];

export interface MudZone {
  id: string;
  rect: Aabb;
}

/** The mud pit leaves a clean 5 m lane along the outer wall. */
export const TRUCK_MUD: readonly MudZone[] = [{ id: "mud_east", rect: aabb(66, 8, 77, 26) }];

/** An ordered checkpoint: a line across the track crossed moving along `forward`. Checkpoint 0 is the start/finish line. */
export interface TruckCheckpoint {
  index: number;
  a: Vec2;
  b: Vec2;
  forward: Vec2;
}

export const START_LINE_X = -36;

export const TRUCK_CHECKPOINTS: readonly TruckCheckpoint[] = [
  { index: 0, a: { x: START_LINE_X, z: PIT.trackEdgeZ }, b: { x: START_LINE_X, z: TRACK.island.minZ }, forward: { x: 1, z: 0 } },
  { index: 1, a: { x: TRACK.island.maxX, z: 29 }, b: { x: TRACK.outerMaxX, z: 29 }, forward: { x: 0, z: 1 } },
  { index: 2, a: { x: 30, z: TRACK.island.maxZ }, b: { x: 30, z: TRACK.outerMaxZ }, forward: { x: -1, z: 0 } },
  { index: 3, a: { x: -30, z: TRACK.island.maxZ }, b: { x: -30, z: TRACK.outerMaxZ }, forward: { x: -1, z: 0 } },
  { index: 4, a: { x: TRACK.outerMinX, z: 12 }, b: { x: TRACK.island.minX, z: 12 }, forward: { x: 0, z: -1 } },
];

/** Race grid behind the start line: two lanes, three rows, facing east. */
export interface GridSlot {
  index: number;
  pose: TruckPose;
  /** A truck whose centre is inside counts as on this slot. */
  rect: Aabb;
}

export const TRUCK_GRID: readonly GridSlot[] = [0, 1, 2, 3, 4, 5].map((i) => {
  const row = Math.floor(i / 2);
  const x = START_LINE_X - 6 - row * 8;
  const z = i % 2 === 0 ? -38.5 : -31.5;
  return { index: i, pose: { x, z, heading: Math.PI / 2 }, rect: aabb(x - 3.5, z - 2.5, x + 3.5, z + 2.5) };
});

/** Lap length along the centre line of the track (metres; for the HUD and the docs). */
export const TRUCK_LAP_METRES = Math.round(2 * (148 + 83) - 4 * (2 - Math.SQRT2) * 9);

export function gridSlotAt(x: number, z: number): GridSlot | null {
  for (const s of TRUCK_GRID) if (x >= s.rect.minX && x <= s.rect.maxX && z >= s.rect.minZ && z <= s.rect.maxZ) return s;
  return null;
}

/**
 * The checkpoint crossed (forward) by a move from `from` to `to`, or -1. Backward crossings never count.
 */
export function checkpointCrossed(from: Vec2, to: Vec2): number {
  for (const cp of TRUCK_CHECKPOINTS) {
    const s0 = (from.x - cp.a.x) * cp.forward.x + (from.z - cp.a.z) * cp.forward.z;
    const s1 = (to.x - cp.a.x) * cp.forward.x + (to.z - cp.a.z) * cp.forward.z;
    if (!(s0 < 0 && s1 >= 0)) continue;
    const t = s0 / (s0 - s1);
    const px = from.x + (to.x - from.x) * t;
    const pz = from.z + (to.z - from.z) * t;
    const minX = Math.min(cp.a.x, cp.b.x) - 1e-6;
    const maxX = Math.max(cp.a.x, cp.b.x) + 1e-6;
    const minZ = Math.min(cp.a.z, cp.b.z) - 1e-6;
    const maxZ = Math.max(cp.a.z, cp.b.z) + 1e-6;
    if (px >= minX && px <= maxX && pz >= minZ && pz <= maxZ) return cp.index;
  }
  return -1;
}

// ---------------------------------------------------------------------------------------------
// Ground
// ---------------------------------------------------------------------------------------------

/** Height of one ramp's surface at (x, z) (0 outside it). */
export function rampHeightAt(r: TruckRamp, x: number, z: number): number {
  const b = r.rect;
  if (x < b.minX || x > b.maxX || z < b.minZ || z > b.maxZ) return 0;
  const u = r.dir.x > 0 ? x - b.minX : r.dir.x < 0 ? b.maxX - x : r.dir.z > 0 ? z - b.minZ : b.maxZ - z;
  const H = r.lipHeight;
  if (r.kind === "pile") {
    const t = Math.min(1, Math.max(0, u / r.up));
    return (H * (1 - Math.cos(2 * Math.PI * t))) / 2;
  }
  if (u <= r.up) return (H * Math.max(0, u)) / r.up;
  if (r.kind === "kicker") return 0;
  if (u <= r.up + r.top) return H;
  const d = u - r.up - r.top;
  return d <= r.down ? H * (1 - d / r.down) : 0;
}

/** Surface height at (x, z): the highest ramp there, else the flat dirt (0). */
export function arenaGroundHeight(x: number, z: number): number {
  let h = 0;
  for (const r of TRUCK_RAMPS) {
    const v = rampHeightAt(r, x, z);
    if (v > h) h = v;
  }
  return h;
}

/** Heights under the front and rear axles. */
export function axleHeights(p: TruckPose): { front: number; rear: number } {
  const half = TRUCK_SPEC.wheelbase / 2;
  const f = truckPoint(p, half, 0);
  const r = truckPoint(p, -half, 0);
  return { front: arenaGroundHeight(f.x, f.z), rear: arenaGroundHeight(r.x, r.z) };
}

/** The height a grounded truck's centre stands at (between its axles). */
export function truckSupportHeight(p: TruckPose): number {
  const a = axleHeights(p);
  return (a.front + a.rear) / 2;
}

export function inMud(x: number, z: number): boolean {
  for (const m of TRUCK_MUD) if (x >= m.rect.minX && x <= m.rect.maxX && z >= m.rect.minZ && z <= m.rect.maxZ) return true;
  return false;
}

// ---------------------------------------------------------------------------------------------
// Driving
// ---------------------------------------------------------------------------------------------

/** A truck's full motion state (the server's authority, the client's prediction). */
export interface TruckMotion extends TruckPose {
  /** m/s along the heading (negative while reversing). */
  speed: number;
  /** Height of the wheels' contact plane above the floor (0 on flat dirt). */
  y: number;
  /** Vertical speed (m/s, up positive). */
  vy: number;
  airborne: boolean;
}

/** Another truck as an obstacle. */
export interface TruckBody extends TruckPose {
  y: number;
}

export interface TruckEnv {
  /** Every OTHER truck on the track (never the one being stepped; not the ones being towed back). */
  others: readonly TruckBody[];
  /** Race countdown: the truck may not move (inputs are acknowledged, gravity still applies). */
  held?: boolean;
}

export interface TruckInput {
  /** +1 gas, -1 brake then reverse once stopped (InputDrive.throttle). */
  throttle: number;
  /** +1 turn right, -1 left (InputDrive.steer). */
  steer: number;
}

const THROTTLE_DEADZONE = 0.05;

function clampUnit(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return v < -1 ? -1 : v > 1 ? 1 : v;
}

function wrap(a: number): number {
  let r = a % (Math.PI * 2);
  if (r > Math.PI) r -= Math.PI * 2;
  if (r <= -Math.PI) r += Math.PI * 2;
  return r;
}

/** New speed after one step of throttle on the ground (mud: half the push, and a drag down to mudMaxSpeed). */
export function nextTruckSpeed(speed: number, throttle: number, dt: number, muddy = false): number {
  const cfg = TRUCK_DRIVE;
  const t = clampUnit(throttle);
  let v = Number.isFinite(speed) ? speed : 0;
  const accel = cfg.accel * (muddy ? TRUCK_PHYSICS.mudAccelFactor : 1);
  if (Math.abs(t) < THROTTLE_DEADZONE) {
    const d = cfg.coastDecel * dt;
    v = Math.abs(v) <= d ? 0 : v - Math.sign(v) * d;
  } else if (t > 0) {
    v = v < 0 ? Math.min(0, v + cfg.brake * t * dt) : v + accel * t * dt;
  } else if (v > 0) {
    v = Math.max(0, v + cfg.brake * t * dt);
  } else {
    v = v + accel * t * dt;
  }
  v = Math.max(-cfg.maxReverse, Math.min(cfg.maxForward, v));
  if (muddy && Math.abs(v) > TRUCK_PHYSICS.mudMaxSpeed) {
    const over = Math.abs(v) - TRUCK_PHYSICS.mudMaxSpeed;
    v -= Math.sign(v) * Math.min(over, TRUCK_PHYSICS.mudDrag * dt);
  }
  return v;
}

export function truckSteerAngle(steer: number, speed: number): number {
  return (clampUnit(steer) * TRUCK_DRIVE.maxSteerRad) / (1 + Math.abs(speed) / TRUCK_DRIVE.steerFadeSpeed);
}

/** The barrier a truck box overlaps (or null). The room bounds count as a barrier too. */
export function truckBlockedBy(p: TruckPose): TruckBarrier | "bounds" | null {
  const o = truckObb(p);
  const reach = Math.hypot(o.halfWidth, o.halfLength);
  if (p.x - reach < B.minX || p.x + reach > B.maxX || p.z - reach < B.minZ || p.z + reach > B.maxZ) {
    // Only the exact box matters at the edges.
    const { fx, fz, rx, rz } = axes(p.heading);
    for (const [f, r] of [
      [1, 1],
      [1, -1],
      [-1, 1],
      [-1, -1],
    ] as const) {
      const x = p.x + fx * f * o.halfLength + rx * r * o.halfWidth;
      const z = p.z + fz * f * o.halfLength + rz * r * o.halfWidth;
      if (x < B.minX || x > B.maxX || z < B.minZ || z > B.maxZ) return "bounds";
    }
  }
  for (const b of TRUCK_BARRIERS) {
    const br = Math.hypot(b.obb.halfWidth, b.obb.halfLength);
    if (Math.abs(b.obb.cx - p.x) >= reach + br || Math.abs(b.obb.cz - p.z) >= reach + br) continue;
    if (obbIntersectsObb(o, b.obb)) return b;
  }
  return null;
}

/** True when the pose is inside the arena and touches no barrier. */
export function truckPoseClear(p: TruckPose): boolean {
  return Number.isFinite(p.x) && Number.isFinite(p.z) && Number.isFinite(p.heading) && truckBlockedBy(p) === null;
}

/** Push-out that separates `p`'s contact circles from every other truck (null when they do not touch). */
function contactPush(p: TruckPose, y: number, others: readonly TruckBody[]): Vec2 | null {
  const mine = truckCircles(p);
  const R = TRUCK_SPEC.circleRadius * 2;
  let px = 0;
  let pz = 0;
  let touched = false;
  for (const o of others) {
    if (Math.abs(o.y - y) > TRUCK_PHYSICS.passOverHeight) continue;
    if (Math.abs(o.x - p.x) > 9 || Math.abs(o.z - p.z) > 9) continue;
    for (const c of mine) {
      for (const d of truckCircles(o)) {
        const dx = c.x - d.x;
        const dz = c.z - d.z;
        const dist = Math.hypot(dx, dz);
        if (dist >= R) continue;
        touched = true;
        const depth = R - dist + 1e-4;
        if (dist < 1e-6) {
          // Exactly on top: push back along our own heading.
          px -= Math.sin(p.heading) * depth;
          pz -= Math.cos(p.heading) * depth;
        } else {
          px += (dx / dist) * depth;
          pz += (dz / dist) * depth;
        }
      }
    }
  }
  return touched ? { x: px, z: pz } : null;
}

/** Height rule for a horizontal move: the surface may not rise more than maxStep above the truck (a ramp's back face blocks). */
function heightAllows(next: TruckPose, y: number): boolean {
  return truckSupportHeight(next) <= y + TRUCK_PHYSICS.maxStep;
}

/**
 * Advance one truck by one input over `dt` seconds. Pure and deterministic: the server is the authority and the
 * client predicts with the same function.
 *
 * Horizontal: kinematic bicycle model (as town cars) with the truck's wheelbase and TRUCK_DRIVE; no steering and no
 * throttle while airborne. Mud halves the push and drags the truck down to 6 m/s. A move whose box would touch a
 * barrier or leave the arena, or climb a face steeper than maxStep, is refused; the truck then tries to slide along
 * the barrier it hit, along x or z, without the rotation, then the rotation alone at a crawl; otherwise it stops.
 * Touching another truck pushes this truck out of it (two circles each) and costs 40% of the speed.
 *
 * Vertical: on the ground the truck follows the surface between its axles (ramps, the table top, the car pile); when
 * its ballistic height (keeping the vertical speed it had while climbing) is above the surface it is airborne:
 * gravity 16 m/s^2 until it touches the surface again. So a ramp's take-off height grows with speed.
 */
export function stepTruck(m: TruckMotion, input: TruckInput, dt: number, env: TruckEnv): TruckMotion {
  const step = Math.min(Math.max(Number.isFinite(dt) ? dt : 0, 0), 0.25);
  if (!Number.isFinite(m.x) || !Number.isFinite(m.z) || !Number.isFinite(m.heading)) return { ...m, speed: 0 };
  if (step === 0) return { ...m };
  const muddy = !m.airborne && inMud(m.x, m.z);
  let speed = env.held ? 0 : m.airborne ? m.speed : nextTruckSpeed(m.speed, input.throttle, step, muddy);
  let pose: TruckPose = { x: m.x, z: m.z, heading: m.heading };

  if (speed !== 0 && !env.held) {
    const turning = !m.airborne && Math.abs(speed) >= TRUCK_DRIVE.minTurnSpeed;
    const dH = turning ? (speed * Math.tan(truckSteerAngle(input.steer, speed)) * step) / TRUCK_SPEC.wheelbase : 0;
    const mid = m.heading + dH / 2;
    const dist = speed * step;
    const dx = Math.sin(mid) * dist;
    const dz = Math.cos(mid) * dist;
    const heading = wrap(m.heading + dH);
    const ok = (p: TruckPose) => truckPoseClear(p) && heightAllows(p, m.y);
    const full: TruckPose = { x: m.x + dx, z: m.z + dz, heading };
    const len = Math.hypot(dx, dz);
    const scaled = (moved: number) => (len > 0 ? speed * Math.min(1, moved / len) : 0);
    const candidates: { p: TruckPose; v: number }[] = [{ p: full, v: speed }];
    const hit = truckBlockedBy(full);
    if (hit && hit !== "bounds" && len > 1e-9) {
      // Slide along the wall that was hit (its long axis).
      const h = hit.obb.heading;
      const tx = Math.sin(h);
      const tz = Math.cos(h);
      const along = dx * tx + dz * tz;
      candidates.push({ p: { x: m.x + tx * along, z: m.z + tz * along, heading }, v: scaled(Math.abs(along)) });
      candidates.push({ p: { x: m.x + tx * along, z: m.z + tz * along, heading: m.heading }, v: scaled(Math.abs(along)) });
    }
    const xFirst = Math.abs(dx) >= Math.abs(dz);
    const slides = xFirst
      ? [
          { x: m.x + dx, z: m.z, moved: Math.abs(dx) },
          { x: m.x, z: m.z + dz, moved: Math.abs(dz) },
        ]
      : [
          { x: m.x, z: m.z + dz, moved: Math.abs(dz) },
          { x: m.x + dx, z: m.z, moved: Math.abs(dx) },
        ];
    for (const s of slides) if (s.moved > 1e-9 && dH !== 0) candidates.push({ p: { x: s.x, z: s.z, heading }, v: scaled(s.moved) });
    if (dH !== 0) candidates.push({ p: { x: m.x + dx, z: m.z + dz, heading: m.heading }, v: speed });
    for (const s of slides) if (s.moved > 1e-9) candidates.push({ p: { x: s.x, z: s.z, heading: m.heading }, v: scaled(s.moved) });
    if (dH !== 0) candidates.push({ p: { x: m.x, z: m.z, heading }, v: Math.sign(speed) * Math.min(Math.abs(speed), TRUCK_PHYSICS.scrapeSpeed) });
    let chosen: { p: TruckPose; v: number } | null = null;
    for (const c of candidates) {
      if (ok(c.p)) {
        chosen = c;
        break;
      }
    }
    if (chosen) {
      pose = chosen.p;
      speed = chosen.v;
    } else {
      speed = 0;
    }
  }

  // Other trucks: push this one out of them (never into a barrier: then it stays where it was and stops).
  if (!env.held) {
    const push = contactPush(pose, m.y, env.others);
    if (push) {
      const pushed: TruckPose = { x: pose.x + push.x, z: pose.z + push.z, heading: pose.heading };
      if (truckPoseClear(pushed) && heightAllows(pushed, m.y)) {
        pose = pushed;
        speed *= TRUCK_PHYSICS.contactSpeedFactor;
      } else if (contactPush({ x: m.x, z: m.z, heading: m.heading }, m.y, env.others) === null) {
        pose = { x: m.x, z: m.z, heading: m.heading };
        speed = 0;
      }
    }
  }

  // Vertical.
  const g = TRUCK_PHYSICS.gravity;
  const ground = truckSupportHeight(pose);
  let y = m.y;
  let vy = m.vy;
  let airborne = m.airborne;
  if (!airborne) {
    const ballistic = y + vy * step - 0.5 * g * step * step;
    if (ballistic > ground + TRUCK_PHYSICS.takeoffEpsilon) {
      airborne = true;
      y = ballistic;
      vy = vy - g * step;
    } else {
      vy = (ground - y) / step;
      y = ground;
    }
  } else {
    y = y + vy * step - 0.5 * g * step * step;
    vy = vy - g * step;
    if (y <= ground) {
      // Touch down; keeping the impact speed lets the next ground step follow a down-slope instead of hopping.
      y = ground;
      airborne = false;
    }
  }
  if (Math.abs(speed) < 1e-9) speed = 0;
  return { x: pose.x, z: pose.z, heading: pose.heading, speed, y, vy, airborne };
}

/**
 * A truck nobody drives (or whose driver stalled): it brakes to a stop by itself and gravity still applies. Used by the
 * server only (a stall is not predicted).
 */
export function coastTruck(m: TruckMotion, dt: number, env: TruckEnv): TruckMotion {
  const throttle = m.airborne || m.speed === 0 ? 0 : m.speed > 0 ? -1 : 1;
  // Braking stops at zero (nextTruckSpeed never brakes through it), so this never starts reversing.
  return stepTruck(m, { throttle, steer: 0 }, dt, env);
}

/** A truck standing still in its bay. */
export function parkedMotion(entry: TruckFleetEntry): TruckMotion {
  return { ...entry.bay, speed: 0, y: 0, vy: 0, airborne: false };
}

/** Visual pitch (nose up positive): along the axles on the ground, along the flight path in the air. */
export function truckPitch(m: TruckMotion): number {
  if (m.airborne) {
    const v = Math.max(2, Math.abs(m.speed));
    return Math.max(-0.45, Math.min(0.45, Math.atan2(m.vy, v) * 0.6));
  }
  const a = axleHeights(m);
  return Math.atan2(a.front - a.rear, TRUCK_SPEC.wheelbase);
}

// ---------------------------------------------------------------------------------------------
// Room template
// ---------------------------------------------------------------------------------------------

/**
 * Monster Truck Arena room (ADR-0016). The exit door is centred on the south wall (z = -60); visitors spawn just inside
 * facing north on the walkway in front of the pit. The template colliders are the pit's (walkers); trucks use
 * TRUCK_BARRIERS.
 */
export function truckArenaTemplate(): InteriorTemplate {
  const b = TRUCK_ARENA_BOUNDS;
  return {
    id: "truck_arena",
    venue: "truck_arena",
    bounds: b,
    spawn: { position: { x: 0, z: b.minZ + 2.4 }, facing: YAW_NORTH },
    exit: { position: { x: 0, z: b.minZ + 1 }, facing: YAW_SOUTH },
    exitDoor: { position: { x: 0, z: b.minZ }, direction: { x: 0, z: -1 }, halfWidth: 1.6 },
    signAnchor: { kind: "storefront", position: { x: 0, y: 5.4, z: b.maxZ - 0.62 }, facing: YAW_SOUTH, width: 18, height: 3.6 },
    colliders: [...TRUCK_PIT_COLLIDERS],
  };
}
